
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  UsersIcon,
  CreditCardIcon,
  DumbbellIcon,
  ActivityIcon,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getDashboardData } from '@/api/dashboard';
import { formatCurrency } from '@/lib/format-currency';

interface DashboardStatsProps {
  selectedGym: string;
}

export function DashboardStats({ selectedGym }: DashboardStatsProps) {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['dashboardData', selectedGym],
    queryFn: () => getDashboardData(selectedGym),
    enabled: !!selectedGym,
  });

  if (isLoading) {
    return (
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, index) => (
          <Card key={index} className="overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 w-24 bg-muted animate-pulse rounded"></div>
              <div className="h-4 w-4 bg-muted animate-pulse rounded-full"></div>
            </CardHeader>
            <CardContent>
              <div className="h-6 w-16 bg-muted animate-pulse rounded mb-2"></div>
              <div className="h-3 w-20 bg-muted animate-pulse rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Count active check-ins (where checkOut is null)
  const activeCheckIns =
    data?.attandanceCount?.filter((checkin) => checkin.checkOut === null)
      ?.length || 0;

  const stats = [
    {
      title: t('dashboard.totalUsers'),
      value: data?.totalUsers.toString() || '0',
      icon: UsersIcon,
      description: data
        ? `${data.totalUsersChange >= 0 ? '+' : ''}${
            data.totalUsersChange
          }% from last month`
        : '',
    },
    {
      title: t('dashboard.activeUsers'),
      value: data?.activeUsers.toString() || '0',
      icon: ActivityIcon,
      description: data
        ? `${data.activeUsersChange >= 0 ? '+' : ''}${
            data.activeUsersChange
          }% from last month`
        : '',
    },
    {
      title: t('dashboard.totalRevenue'),
      value: formatCurrency(data?.totalRevenue || 0, 'ALL'),
      euro: formatCurrency((data?.totalRevenue ?? 0) / 100, 'EUR'),
      icon: CreditCardIcon,
      description: data
        ? `${data.totalRevenueChange >= 0 ? '+' : ''}${
            data.totalRevenueChange
          }% from last month`
        : '',
    },
    {
      title: t('dashboard.activeCheckIns'),
      value: activeCheckIns.toString(),
      icon: DumbbellIcon,
      description: t('dashboard.currentlyInGym'),
    },
  ];

  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      {stats.map((stat, index) => (
        <Card key={index} className="overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
            <stat.icon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stat.value}</div>
            <div className="text-2xl font-bold">{stat.euro}</div>
            <p className="text-xs text-muted-foreground">{stat.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
